import React from 'react';
import { App, GQL } from '@gin-betting/core';
import cn from 'classnames';

import { LIVE_SPORTEVENT_STATUSES } from 'app-constants';
import {
    SmartLink,
    SportEventInViewSubscription,
    SportEventSeoMicromarking,
} from 'components';
import { AnalyticAttrs, AnalyticAttrsCtx, Category } from 'services/analytics';
import { Score } from 'ui-lib';
import FixtureData from './FixtureData';
import TeamsTitle from './TeamsTitle';
import TopMarkets from './TopMarkets';
import TournamentHeader from './TournamentHeader';
import useOverviewRow from './useOverviewRow';

import './overviewRow.scss';

interface Props {
    sportEventId: string;
    prevSportEventId: string | undefined;
}

const ANALYTICS: Partial<AnalyticAttrs> = {
    'data-category': Category.LineSportEvent,
};

const OverviewRow: React.FC<Props> = ({ sportEventId, prevSportEventId }) => {
    const {
        match,
        route,
        isStreamAvailable,
        isExtraSmall,
        isSmall,
        isMedium,
    } = useOverviewRow({ sportEventId });

    if (!match) return null;

    const { slug, version, sportId, status, startTime } = match;

    const marketCount = isSmall ? 1 : isMedium ? 2 : 3;

    const linkToMatch = `${route}${App.Routes.MATCH}${slug}`;
    const isLive = LIVE_SPORTEVENT_STATUSES.includes(status);
    const isPrematchMobileView = !isLive && isExtraSmall;
    const isResulted = GQL.SportEventStatus.ENDED === status;

    return (
        <AnalyticAttrsCtx.Provider value={ANALYTICS}>
            <TournamentHeader
                prevSportEventId={prevSportEventId}
                route={route}
                sportId={sportId}
                sportEventId={sportEventId}
            />
            <SmartLink
                to={linkToMatch}
                styleName={cn('container', {
                    'is-prematch-mobile-view': isPrematchMobileView,
                    'is-extra-small': isExtraSmall,
                })}
            >
                <SportEventSeoMicromarking sportEventId={sportEventId} />
                <SportEventInViewSubscription
                    matchId={sportEventId}
                    version={version}
                />
                <div styleName="info">
                    <div styleName="fixture">
                        <FixtureData
                            sportEvent={match}
                            isStreamAvailable={isStreamAvailable}
                            withDate={!isPrematchMobileView}
                        />
                    </div>
                    <div styleName="titles">
                        <TeamsTitle
                            sportEventId={sportEventId}
                            isPrematchMobileView={isPrematchMobileView}
                            startTime={startTime}
                        />
                    </div>
                </div>
                {(isLive || isResulted) && (
                    <div styleName="score">
                        <Score sportEventId={sportEventId} />
                    </div>
                )}
                <div styleName="markets">
                    <TopMarkets
                        sportEventId={sportEventId}
                        count={marketCount}
                        withName={!isExtraSmall}
                    />
                </div>
            </SmartLink>
        </AnalyticAttrsCtx.Provider>
    );
};

export default OverviewRow;
